import { NavLink, Outlet } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  Package, 
  ShoppingCart, 
  MessageCircle, 
  Settings,
  Leaf,
  FileText,
  Warehouse,
  LogOut,
  UserCheck,
  Activity,
  IndianRupee,
  RotateCcw,
  Eye,
  Megaphone,
  Target
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, permission: "dashboard" },
  { path: "/customers", label: "Customers", icon: Users, permission: "customers" },
  { path: "/products", label: "Products", icon: Package, permission: "products" },
  { path: "/orders", label: "Orders", icon: ShoppingCart, permission: "orders" },
  { path: "/warehouse", label: "Warehouse", icon: Warehouse, permission: "warehouse" },
  { path: "/crm", label: "CRM", icon: UserCheck, permission: "crm" },
  { path: "/receipts", label: "Receipts", icon: IndianRupee, permission: "crm" },
  { path: "/returns", label: "Returns", icon: RotateCcw, permission: "crm" },
  { path: "/marketing", label: "Marketing", icon: Megaphone, permission: "customers" },
  { path: "/strategy", label: "Strategy", icon: Target, roles: ["owner", "admin"] },
  { path: "/customer-preview", label: "Customer Preview", icon: Eye, permission: "customers" },
  { path: "/conversations", label: "Conversations", icon: MessageCircle, permission: "conversations" },
  { path: "/reports", label: "Reports", icon: FileText, permission: "reports" },
  { path: "/settings", label: "Settings", icon: Settings, permission: "settings" },
  { path: "/activity-log", label: "Activity Log", icon: Activity, permission: "settings" },
];

export default function Layout() {
  const { user, logout, hasPermission } = useAuth();

  // Filter nav items based on user permissions and role
  const filteredNavItems = navItems.filter(item => {
    if (item.roles) return user && item.roles.includes(user.role);
    return hasPermission(item.permission);
  });

  return (
    <div className="flex min-h-screen" data-testid="layout-container">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-border/60 flex flex-col" data-testid="sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-border/60">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-white">
              <img 
                src="https://customer-assets.emergentagent.com/job_cashew-sales/artifacts/9nv8ikuy_Untitled%20design.png" 
                alt="Purelife Logo" 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className="font-bold text-lg text-primary" style={{ fontFamily: 'Manrope' }}>
                Purelife Bot
              </h1>
              <p className="text-xs text-muted-foreground">B2B Sales Portal</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-item ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </ScrollArea>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-border/60">
          {user && (
            <div className="space-y-3">
              <div className="text-sm">
                <p className="font-medium">{user.username}</p>
                <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full gap-2"
                onClick={logout}
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
